<script setup lang="ts">
import type { AlgoliaProductType } from './WrapperProductTile.props'
import type { XGenRecommendationsProps } from './XGenRecommendations.props.ts'

const props = withDefaults(defineProps<XGenRecommendationsProps>(), {
  cacheXGenResponse: true,
})

/**
 * Composables for fetching recommendations and prices
 */
const { recommendations } = useXgen()
const { skuPrices, fetchSkuPrices } = useProductTileClPrices()

/**
 * Reactive state for showing more recommendations
 *
 * @type {Ref<boolean>}
 */
const showMore = ref(false)

const { data: products, status } = useLazyAsyncData<AlgoliaProductType[]>(
  `xgen-recommendations-${props.prediction}`,
  async nuxtApp => {
    if (
      !props.cacheXGenResponse &&
      nuxtApp?.payload.data[`xgen-recommendations-${props.prediction}`]
    ) {
      // Remove older cached data just to make sure
      nuxtApp.payload.data[`xgen-recommendations-${props.prediction}`] =
        undefined
    }
    await sleep(1000) // Delay to make sure that the GA event state is updated
    const recommendedProducts = await recommendations({
      predictionID: props.prediction,
    })
    return recommendedProducts
  },
  {
    default: () => [],
    server: false,
    getCachedData: (key, nuxtApp) =>
      !props.cacheXGenResponse
        ? undefined
        : nuxtApp.isHydrating
          ? nuxtApp.payload?.data?.[key]
          : nuxtApp.static?.data?.[key],
  }
)

/**
 * Computed property to add Commerce Layer prices to products
 *
 * @type {ComputedRef<Array>}
 */
const productsWithCommerceLayerPrices = computed(() => {
  return (
    products.value?.map(product => {
      return {
        ...product,
        clPrices: skuPrices.value?.[product.size[0].SKU ?? ''],
      }
    }) ?? []
  )
})

/**
 * Client-side watcher to fetch SKU prices and trigger GA event
 */
if (import.meta.client) {
  watch(
    products,
    productList => {
      const productItemsDefaultSkuCodes =
        productList
          ?.map(item => item.size?.at(0)?.SKU)
          ?.filter(item => isNonNullable(item)) ?? []

      if (productItemsDefaultSkuCodes.length) {
        useGAxGenViewItemListEvent(
          products.value?.slice?.(0, props.initialClFetch) ?? [],
          props.prediction
        )
        fetchSkuPrices(
          productItemsDefaultSkuCodes.slice(0, props.initialClFetch)
        )
      }
    },
    {
      once: true,
    }
  )
}

/**
 * Method to load more recommendations and trigger events
 */
const loadMore = () => {
  const productItemsDefaultSkuCodes =
    products.value
      ?.map(item => item.size?.at(0)?.SKU)
      ?.filter(item => isNonNullable(item)) ?? []

  fetchSkuPrices(productItemsDefaultSkuCodes)
  showMore.value = !showMore.value
  useGAxGenViewItemListEvent(products.value ?? [], props.prediction)
}
</script>
<template>
  <div class="contents">
    <slot
      :products="productsWithCommerceLayerPrices"
      :load-more="loadMore"
      :status="status"
      :show-more="showMore"
    />
  </div>
</template>
